import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { Rng } from './rng';
import { bakeAvatar, Crowd, type CrowdSlot } from './crowd';
import { retry } from '../engine/retry';

/**
 * Street population: Rocketbox avatars with shared animation clips.
 * Walkers use the road edge (where people actually walk on a Lagos street without pavements)
 * and the frontage edge; standers wait at shop fronts on their phones, looking around, or in
 * pairs talking. Walk speed comes from the clip's own root motion, so feet don't slide.
 * Rendering goes through the GPU crowd (baked animation textures + instancing).
 */

const AVATARS: { file: string; sex: 'm' | 'f' }[] = [
  { file: 'Male_Adult_04', sex: 'm' }, { file: 'Male_Adult_12', sex: 'm' }, { file: 'Male_Adult_18', sex: 'm' },
  { file: 'Construction_Male_03', sex: 'm' }, { file: 'Sports_Male_03', sex: 'm' }, { file: 'Business_Male_05', sex: 'm' },
  { file: 'Business_Female_01', sex: 'f' }, { file: 'Pilot_Female_02', sex: 'f' },
  { file: 'Female_Party_02', sex: 'f' }, { file: 'Female_Adult_06', sex: 'f' },
];
/** how often each cast member turns up among their sex (a pilot's uniform on a market street: now and then) */
const AVATAR_WEIGHT: Record<string, number> = { Pilot_Female_02: 0.35 };
const CLIPS = ['walk', 'walk_slow', 'idle', 'look', 'phone', 'talk', 'listen', 'sit'] as const;
type ClipName = (typeof CLIPS)[number];
/** metres per second of the in-place walk cycles (from tools/build_people.sh speeds.txt) */
export const WALK_SPEED: Record<string, number> = { m_walk: 1.012, f_walk: 1.214, m_walk_slow: 0.821, f_walk_slow: 0.887, m_run: 2.88, f_run: 2.765 };

interface Person {
  slot: CrowdSlot;
  sex: 'm' | 'f';
  seat?: { write(out: THREE.Matrix4): void };
  pos: THREE.Vector3;
  rotY: number;
  scale: number;
  walking: boolean;
  dir: 1 | -1;
  speed: number;
  minX: number;
  maxX: number;
  /** the person's street frame (local → world) and its inverse */
  mat: THREE.Matrix4;
  inv: THREE.Matrix4;
  /** walking line (local z) and the sidestep currently taken to get round the player */
  baseZ: number;
  offZ: number;
  /** current world position (feet) */
  world: THREE.Vector3;
  /** works here (a cashier): not a stranger on the street */
  staff?: boolean;
}

export interface PeopleOptions {
  half: number;
  walkers: number;
  standers: number;
  shopFronts: THREE.Vector3[];
  /** |z| of walking lines: road edge and frontage edge */
  walkLines: number[];
  /** vehicle seats to fill with seated drivers/riders (each writes a world matrix per frame) */
  seats?: { write(out: THREE.Matrix4): void }[];
  /** street frame → world (default identity) */
  matrix?: THREE.Matrix4;
  /** where walkers leave and re-enter the street, local x (default 20 m past each end) */
  walkEnds?: [number, number];
  /** people at fixed spots (cashiers behind counters), street-local */
  staff?: { pos: THREE.Vector3; rotY: number }[];
}

type Avatar = { file: string; sex: 'm' | 'f'; scene: THREE.Group };

let assets: Promise<{ avatars: Avatar[]; clipMap: Map<string, THREE.AnimationClip> }> | null = null;
const bakedCache = new Map<THREE.Object3D, ReturnType<typeof bakeAvatar>>();

export class People {
  readonly group = new THREE.Group();
  private people: Person[] = [];
  private crowd: Crowd;
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private tmp = new THREE.Vector3();
  private up = new THREE.Vector3(0, 1, 0);

  /** A full-detail Rocketbox avatar (for the player when no character has been made). */
  static async loadFullAvatar(file: string): Promise<THREE.Group> {
    const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    return (await retry(() => loader.loadAsync(`${import.meta.env.BASE_URL}people/avatars/${file}.glb`))).scene;
  }

  static async load(o: PeopleOptions | PeopleOptions[], seed = 'people'): Promise<People> {
    // avatars and clips load once per page, however many streets have people
    assets ??= (async () => {
      const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
      const base = `${import.meta.env.BASE_URL}people`;
      const [avatars, clips] = await Promise.all([
        // the crowd uses simplified meshes (crowd/); full ones (avatars/) are only for a stock-avatar player
        Promise.all(AVATARS.map((a) => retry(() => loader.loadAsync(`${base}/crowd/${a.file}.glb`)).then((g) => ({ ...a, scene: g.scene })))),
        Promise.all(['m', 'f'].flatMap((s) => CLIPS.map((c) => retry(() => loader.loadAsync(`${base}/anims/${s}_${c}.glb`)).then((g) => {
          const clip = g.animations[0];
          clip.name = `${s}_${c}`;
          return clip;
        })))),
      ]);
      for (const a of avatars) {
        a.scene.traverse((ob) => {
          const m = ob as THREE.SkinnedMesh;
          if (m.isSkinnedMesh) for (const mat of [m.material].flat() as THREE.MeshStandardMaterial[]) {
            mat.envMapIntensity = 0.6;
            // the hair (and lashes): cards cut out of a texture. Shiny, the flat cards caught the sky as pale blue
            // stripes through curly hair: matte, a firmer cut-out, no environment sheen
            if (/opacity/i.test(mat.name)) { mat.roughness = 1; mat.metalness = 0; mat.envMapIntensity = 0.08; mat.alphaTest = 0.6; mat.needsUpdate = true; }
          }
        });
      }
      // keep only tracks that drive nodes the avatars actually have (clips include face/finger "nub" helpers)
      const nodes = new Set<string>();
      avatars[0].scene.traverse((ob) => nodes.add(ob.name));
      for (const c of clips) c.tracks = c.tracks.filter((tr) => nodes.has(tr.name.slice(0, tr.name.lastIndexOf('.'))));
      return { avatars, clipMap: new Map(clips.map((c) => [c.name, c])) };
    })();
    const { avatars, clipMap } = await assets;
    return new People(avatars, clipMap, o, seed);
  }

  private constructor(
    readonly avatars: Avatar[],
    readonly clips: Map<string, THREE.AnimationClip>,
    streets: PeopleOptions | PeopleOptions[],
    seed: string,
  ) {
    type Spec = { avatar: number; clip: ClipName; walking: boolean; pos: THREE.Vector3; rotY: number; dir: 1 | -1; ts: number; seat?: { write(out: THREE.Matrix4): void }; street: number; staff?: boolean };
    const specs: Spec[] = [];
    const list = [streets].flat();
    const rngs: Rng[] = [];
    list.forEach((o, street) => {
    // each street keeps its own random sequence, so adding a street never reshuffles another
    const rng = new Rng(street === 0 ? seed : `${seed}-${street}`);
    rngs.push(rng);
    // half women, half men (picking evenly from the cast, six men to two women, the street was three-quarters men),
    // then someone of that sex
    const bySex = (sx: 'm' | 'f') => avatars.map((a, i) => [i, AVATAR_WEIGHT[a.file] ?? 1] as [number, number]).filter(([i]) => avatars[i].sex === sx);
    const cast = { m: bySex('m'), f: bySex('f') };
    const pickAvatar = () => rng.weighted(cast[rng.chance(0.5) ? 'f' : 'm']);
    const push = (s: Omit<Spec, 'street'>) => specs.push({ ...s, street });

    for (let i = 0; i < o.walkers; i++) {
      const dir: 1 | -1 = rng.chance(0.5) ? 1 : -1;
      const side = rng.chance(0.5) ? 1 : -1;
      const line = rng.pick(o.walkLines) + rng.range(-0.2, 0.2);
      push({
        avatar: pickAvatar(), clip: rng.chance(0.35) ? 'walk_slow' : 'walk', walking: true, dir, ts: rng.range(0.9, 1.1),
        pos: new THREE.Vector3(rng.range(-o.half, o.half), 0, side * line), rotY: dir === 1 ? Math.PI / 2 : -Math.PI / 2,
      });
    }
    const spots = [...o.shopFronts].sort(() => rng.next() - 0.5);
    for (let i = 0; i < Math.min(o.standers, spots.length); i++) {
      const p = spots[i];
      const towardsRoad = p.z > 0 ? Math.PI : 0;
      if (rng.chance(0.3)) {
        // a pair in conversation, facing each other
        const along = rng.range(0, Math.PI * 2);
        const dx = Math.cos(along) * 0.55, dz = Math.sin(along) * 0.55;
        const a = new THREE.Vector3(p.x + dx, 0, p.z + dz), b = new THREE.Vector3(p.x - dx, 0, p.z - dz);
        push({ avatar: pickAvatar(), clip: 'talk', walking: false, dir: 1, ts: 1, pos: a, rotY: Math.atan2(b.x - a.x, b.z - a.z) });
        push({ avatar: pickAvatar(), clip: 'listen', walking: false, dir: 1, ts: 1, pos: b, rotY: Math.atan2(a.x - b.x, a.z - b.z) });
      } else {
        push({
          avatar: pickAvatar(), clip: rng.weighted([['idle', 3], ['phone', 3], ['look', 2]] as const), walking: false, dir: 1, ts: 1,
          pos: new THREE.Vector3(p.x + rng.range(-0.8, 0.8), 0, p.z + rng.range(-0.3, 0.3)), rotY: towardsRoad + rng.range(-0.8, 0.8),
        });
      }
    }

    for (const s of o.staff ?? []) push({ avatar: pickAvatar(), clip: 'idle', walking: false, dir: 1, ts: 1, pos: s.pos.clone().setY(0), rotY: s.rotY, staff: true });
    for (const seat of o.seats ?? []) {
      push({ avatar: pickAvatar(), clip: 'sit', walking: false, dir: 1, ts: 1, pos: new THREE.Vector3(), rotY: 0, seat });
    }
    });

    // bake each avatar with the clips for its sex, then size the instanced meshes to the crowd
    const baked = avatars.map((a) => {
      let bk = bakedCache.get(a.scene);
      if (!bk) bakedCache.set(a.scene, (bk = bakeAvatar(a.scene, CLIPS.map((c) => clips.get(`${a.sex}_${c}`)!))));
      return bk;
    });
    const capacity = avatars.map((_, i) => specs.filter((s) => s.avatar === i).length);
    this.crowd = new Crowd(baked, capacity);
    this.group.add(this.crowd.group);

    for (const s of specs) {
      const o = list[s.street], rng = rngs[s.street];
      const sex = avatars[s.avatar].sex;
      const slot = this.crowd.allocate(s.avatar);
      const clipName = `${sex}_${s.clip}`;
      this.crowd.setAnimation(slot, clipName, rng.range(0, this.crowd.clipDuration(s.avatar, clipName)), s.ts);
      const mat = o.matrix ?? new THREE.Matrix4();
      const [minX, maxX] = o.walkEnds ?? [-o.half - 20, o.half + 20];
      if (s.walking) s.pos.x = Math.min(maxX, Math.max(minX, s.pos.x));
      this.people.push({
        slot, sex, seat: s.seat, pos: s.pos, rotY: s.rotY, scale: rng.range(0.95, 1.05), walking: s.walking, dir: s.dir,
        speed: s.walking ? WALK_SPEED[clipName] * s.ts : 0, minX, maxX, mat, inv: mat.clone().invert(),
        baseZ: s.pos.z, offZ: 0, world: s.pos.clone().applyMatrix4(mat), staff: s.staff,
      });
    }
  }

  get count() { return this.people.length; }

  /** walkable surface height (world): set by the district */
  heightAt: ((x: number, z: number) => number) | null = null;
  /** the player (world), whom walkers step around */
  avoid: THREE.Vector3 | null = null;
  /** solid things in their way (world boxes: your car, your ride at the kerb): walkers go round, on the pavement side */
  blocks: { minX: number; maxX: number; minZ: number; maxZ: number }[] = [];
  /** the street's solids (walls, kiosks, benches, posts), in a coarse grid: nobody walks or stands inside one */
  private solids = new Map<number, { minX: number; maxX: number; minZ: number; maxZ: number }[]>();
  private hidden = new Set<unknown>();
  setSolids(boxes: { minX: number; maxX: number; minZ: number; maxZ: number }[]) {
    this.solids.clear();
    for (const b of boxes) for (let cx = Math.floor(b.minX / 4); cx <= Math.floor(b.maxX / 4); cx++) for (let cz = Math.floor(b.minZ / 4); cz <= Math.floor(b.maxZ / 4); cz++) {
      const k = cx * 100003 + cz; let l = this.solids.get(k); if (!l) this.solids.set(k, (l = [])); l.push(b);
    }
    // (someone generated standing inside one: not there)
    this.hidden.clear();
    for (const p of this.people) { if (p.seat || p.walking) continue; p.world.copy(p.pos).setY(0).applyMatrix4(p.mat); if (this.solidAt(p.world.x, p.world.z)) this.hidden.add(p.slot); }
  }
  private solidAt(x: number, z: number): boolean {
    const l = this.solids.get(Math.floor(x / 4) * 100003 + Math.floor(z / 4));
    return !!l && l.some((b) => x > b.minX + 0.12 && x < b.maxX - 0.12 && z > b.minZ + 0.12 && z < b.maxZ - 0.12);
  }

  /** People within r of a point (world), for collisions. Seated riders don't count. */
  near(x: number, z: number, r: number): { id: number; x: number; z: number }[] {
    const out: { id: number; x: number; z: number }[] = [];
    this.people.forEach((p, id) => {
      if (p.seat) return;
      if ((p.world.x - x) ** 2 + (p.world.z - z) ** 2 < r * r) out.push({ id, x: p.world.x, z: p.world.z });
    });
    return out;
  }

  /** Where someone is (world, feet). */
  worldPos(id: number): THREE.Vector3 | null { return this.people[id]?.world ?? null; }

  /** Pedestrians walking in the carriageway (world), for drivers to stop for. */
  inRoad(roadHalf: number): { x: number; z: number }[] {
    return this.people.filter((p) => p.walking && Math.abs(p.pos.z) < roadHalf).map((p) => ({ x: p.world.x, z: p.world.z }));
  }

  /** The nearest person standing around (not walking, not in a vehicle) within reach. World space. */
  nearestStanding(at: THREE.Vector3, reach: number): { index: number; pos: THREE.Vector3; sex: 'm' | 'f' } | null {
    let best: { index: number; pos: THREE.Vector3; sex: 'm' | 'f' } | null = null, bd = reach * reach;
    const w = new THREE.Vector3();
    this.people.forEach((p, index) => {
      if (p.walking || p.seat || p.staff) return;
      w.copy(p.pos).applyMatrix4(p.mat);
      const d = (w.x - at.x) ** 2 + (w.z - at.z) ** 2;
      if (d < bd) { bd = d; best = { index, pos: w.clone(), sex: p.sex }; }
    });
    return best;
  }

  /** Move someone (e.g. to where they'll hold your phone); returns how to put them back. World space. */
  relocate(index: number, to: THREE.Vector3, faceAt: THREE.Vector3): () => void {
    const p = this.people[index];
    if (!p) return () => {};
    const was = p.pos.clone(), rot = p.rotY;
    const lt = to.clone().applyMatrix4(p.inv), lf = faceAt.clone().applyMatrix4(p.inv);
    p.pos.set(lt.x, 0, lt.z);
    p.rotY = Math.atan2(lf.x - lt.x, lf.z - lt.z);
    return () => { p.pos.copy(was).setY(0); p.rotY = rot; };
  }

  /** Turn someone to face a point (when the player talks to them). World space. */
  face(index: number, at: THREE.Vector3) {
    const p = this.people[index];
    if (!p) return;
    const l = at.clone().applyMatrix4(p.inv);
    p.rotY = Math.atan2(l.x - p.pos.x, l.z - p.pos.z);
  }

  /** the camera and the head it looks at (world), set each frame by the street: people in between step out of shot */
  view: { cam: THREE.Vector3; head: THREE.Vector3 } | null = null;
  private inView(w: THREE.Vector3): boolean {
    const v = this.view;
    if (!v) return false;
    const cx = v.cam.x - w.x, cz = v.cam.z - w.z;
    if (cx * cx + cz * cz < 0.75 * 0.75) return true; // by the lens (at any height: from above you'd see into their hair)
    // on the line from your head to the camera (a body is ~0.3 m round), past your own shoulder
    const dx = v.cam.x - v.head.x, dz = v.cam.z - v.head.z, L2 = dx * dx + dz * dz;
    if (L2 < 0.01) return false;
    const t = ((w.x - v.head.x) * dx + (w.z - v.head.z) * dz) / L2;
    if (t < 0.15 || t > 1.05) return false;
    const px = v.head.x + dx * t - w.x, pz = v.head.z + dz * t - w.z, y = v.head.y + (v.cam.y - v.head.y) * t;
    return px * px + pz * pz < 0.42 * 0.42 && y < w.y + 2.0;
  }

  update(dt: number) {
    for (const p of this.people) {
      if (p.seat) {
        p.seat.write(this.m);
        this.crowd.setMatrix(p.slot, this.m);
        continue;
      }
      if (p.walking) {
        const x0 = p.pos.x;
        p.pos.x += p.speed * p.dir * dt;
        if (p.pos.x > p.maxX) p.pos.x = p.minX;
        if (p.pos.x < p.minX) p.pos.x = p.maxX;
        // the player is in the way: step aside (towards whichever side has more room), then drift back
        let want = 0;
        if (this.avoid) {
          this.tmp.copy(this.avoid).applyMatrix4(p.inv);
          const ahead = (this.tmp.x - p.pos.x) * p.dir, side = this.tmp.z - (p.baseZ + p.offZ);
          if (ahead > -0.3 && ahead < 2.6 && Math.abs(side) < 0.75) want = side > 0 ? -0.85 : 0.85;
          else if (Math.abs(p.offZ) > 0.05 && ahead > -0.6 && ahead < 1 && Math.abs(this.tmp.z - p.baseZ) < 1) want = p.offZ; // still passing: hold the line
        }
        // a car at the kerb (yours, your ride): go round it on the pavement side, from a couple of metres before it
        for (const b of this.blocks) {
          const cx = (b.minX + b.maxX) / 2, cz = (b.minZ + b.maxZ) / 2;
          if ((p.world.x - cx) ** 2 + (p.world.z - cz) ** 2 > 64) continue;
          let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
          for (const [wx, wz] of [[b.minX, b.minZ], [b.maxX, b.minZ], [b.minX, b.maxZ], [b.maxX, b.maxZ]]) {
            this.tmp.set(wx, 0, wz).applyMatrix4(p.inv);
            x0 = Math.min(x0, this.tmp.x); x1 = Math.max(x1, this.tmp.x); z0 = Math.min(z0, this.tmp.z); z1 = Math.max(z1, this.tmp.z);
          }
          const lead = p.dir > 0 ? x0 - p.pos.x : p.pos.x - x1, past = p.dir > 0 ? p.pos.x - x1 : x0 - p.pos.x;
          if (lead > 2.2 || past > 0.4) continue; // not there yet, or already by it
          const z = p.baseZ + want;
          if (z < z0 - 0.35 || z > z1 + 0.35) continue; // their line misses it
          const out = p.baseZ >= (z0 + z1) / 2 ? z1 + 0.45 : z0 - 0.45; // the pavement side
          want = out - p.baseZ;
        }
        p.offZ = THREE.MathUtils.damp(p.offZ, want, 5, dt);
        p.pos.z = p.baseZ + p.offZ;
        // a wall, a kiosk, a bench in their way: they turn round rather than walk through it
        // (a lane that runs inside one, a fence along the pavement: not drawn while they're in it, and no turning on the spot)
        if (this.solids.size) {
          this.tmp.copy(p.pos).setY(0).applyMatrix4(p.mat);
          if (this.solidAt(this.tmp.x, this.tmp.z)) {
            const was = this.tmp.set(x0, 0, p.pos.z).applyMatrix4(p.mat);
            if (this.solidAt(was.x, was.z)) this.hidden.add(p.slot);
            else { p.pos.x = x0; p.dir = p.dir > 0 ? -1 : 1; p.rotY += Math.PI; }
          } else this.hidden.delete(p.slot);
        }
      }
      p.world.copy(p.pos).setY(0).applyMatrix4(p.mat);
      if (this.heightAt && (p.walking || p.pos.y === 0)) p.pos.y = this.heightAt(p.world.x, p.world.z);
      p.world.y = p.pos.y;
      this.q.setFromAxisAngle(this.up, p.rotY);
      // in the camera's way (right by the lens, or between it and you): not drawn this frame, rather than the
      // camera looking out through their head
      const s = this.inView(p.world) || this.hidden.has(p.slot) ? 0 : p.scale;
      this.m.compose(p.pos, this.q, new THREE.Vector3(s, s, s)).premultiply(p.mat);
      this.crowd.setMatrix(p.slot, this.m);
    }
    this.crowd.update(dt);
  }

  private sphere = new THREE.Sphere(new THREE.Vector3(), 2.5);
  /** who's drawn this frame: in view (a little round it: their shadows fall into it) and within `far` m of the camera */
  cull(frustum: THREE.Frustum, cam: THREE.Vector3, far: number) {
    const s = this.sphere, f2 = far * far;
    this.crowd.cull((x, y, z) => {
      if ((x - cam.x) ** 2 + (z - cam.z) ** 2 > f2) return false;
      s.center.set(x, y + 0.9, z);
      return frustum.intersectsSphere(s);
    });
  }
}
